import * as React from 'react';
import { api } from '../api';
import { useApi, useAsyncAction } from '../hooks';
import { Icon } from '../icons.jsx';
import { Badge, Button, Field, Input } from '../ui.jsx';

const STEPS = ['Find center', 'Confirm record', 'Set up profile', 'Review'];

const StepTracker = ({ step }) => (
  <ol className="onboarding-steps" aria-label="Onboarding progress">
    {STEPS.map((label, index) => (
      <li key={label} className={index <= step ? 'is-active' : ''}>
        <span>{index < step ? <Icon name="check" size={13}/> : index + 1}</span>
        <small>{label}</small>
      </li>
    ))}
  </ol>
);

export const OnboardingScreen = ({ go }) => {
  const [step, setStep] = React.useState(0);
  const [search, setSearch] = React.useState('Sunnyside');
  const [selectedId, setSelectedId] = React.useState('sunnyside');
  const [confirmed, setConfirmed] = React.useState(false);
  const [accepted, setAccepted] = React.useState(false);
  const [profile, setProfile] = React.useState({
    description: 'A neighborhood center where children learn through play and outdoor exploration.',
    hours: 'Mon-Fri · 7:30 AM-5:30 PM',
    languages: 'English, Spanish',
    infantSpots: 2,
    preKSpots: 1,
  });
  const {
    data: providers,
    loading,
    error,
    reload,
  } = useApi(() => api.providers.search({}), []);
  const finishAction = useAsyncAction(async () => {
    const provider = providers.find(item => item.id === selectedId);
    if (!provider) throw new Error('Select a provider before continuing');
    await api.providers.updateProfile(selectedId, {
      description: profile.description,
      hours: profile.hours,
      languages: profile.languages,
    });
    await api.providers.updateAvailability(selectedId, {
      Infant: {
        on: profile.infantSpots > 0,
        count: profile.infantSpots,
        note: profile.infantSpots > 0 ? 'Available now' : 'Waitlist',
      },
      'Pre-K': {
        on: profile.preKSpots > 0,
        count: profile.preKSpots,
        note: profile.preKSpots > 0 ? 'Available now' : 'Waitlist',
      },
    });
    return provider;
  });

  const matches = (providers || []).filter(provider =>
    `${provider.name} ${provider.address}`.toLowerCase().includes(search.toLowerCase()),
  );
  const selected = (providers || []).find(provider => provider.id === selectedId);

  const finish = async () => {
    try {
      await finishAction.run();
      go('dashboard');
    } catch {
      // useAsyncAction exposes the error on the review step.
    }
  };

  const canContinue =
    step === 0
      ? Boolean(selected)
      : step === 1
        ? confirmed
        : step === 2
          ? Boolean(
              profile.description.trim() &&
                profile.hours.trim() &&
                profile.languages.trim(),
            )
          : accepted;

  return (
    <div className="onboarding-page route-in">
      <header className="onboarding-header">
        <button type="button" onClick={() => go('home')} aria-label="Find My Childcare home">
          <img src="/assets/logo.png" alt=""/>
          <span>Find My Childcare</span>
        </button>
        <Badge tone="yellow">Provider setup demonstration</Badge>
      </header>

      <main className="onboarding-shell">
        <StepTracker step={step}/>
        <section className="onboarding-card" aria-live="polite">
          {step === 0 && (
            <>
              <div className="onboarding-title">
                <span>Step 1</span>
                <h1>Find your official provider record</h1>
                <p>Search by center name or address. Official information stays connected to Pennsylvania’s record.</p>
              </div>
              <Field id="provider-search" label="Center name or address">
                <Input
                  icon="search"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Start typing a center name"
                />
              </Field>
              {error && (
                <div className="onboarding-error" role="alert">
                  We couldn’t load provider records: {error.message}
                  <button type="button" onClick={reload}>Try again</button>
                </div>
              )}
              <div className="onboarding-matches">
                {loading
                  ? <span>Finding providers…</span>
                  : matches.map(provider => (
                      <button
                        key={provider.id}
                        type="button"
                        className={selectedId === provider.id ? 'is-selected' : ''}
                        onClick={() => setSelectedId(provider.id)}
                      >
                        <span className="onboarding-match-icon"><Icon name="home" size={18}/></span>
                        <span>
                          <strong>{provider.name}</strong>
                          <small>{provider.address}</small>
                        </span>
                        {selectedId === provider.id && <Icon name="check-circle" size={20}/>}
                      </button>
                    ))}
              </div>
            </>
          )}

          {step === 1 && selected && (
            <>
              <div className="onboarding-title">
                <span>Step 2</span>
                <h1>Confirm this is your center</h1>
                <p>These details come from the official provider record and cannot be edited here.</p>
              </div>
              <div className="onboarding-official-record">
                <div>
                  <span>Provider name</span>
                  <strong>{selected.name}</strong>
                </div>
                <div>
                  <span>Address</span>
                  <strong>{selected.address}</strong>
                </div>
                <div>
                  <span>Program</span>
                  <strong>Certified child care</strong>
                </div>
                <div>
                  <span>Subsidy</span>
                  <strong>{selected.subsidy ? 'Accepted' : 'Not listed'}</strong>
                </div>
              </div>
              <label className="onboarding-confirm">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={event => setConfirmed(event.target.checked)}
                />
                <span>I am authorized to manage this center’s Find My Childcare profile.</span>
              </label>
            </>
          )}

          {step === 2 && selected && (
            <>
              <div className="onboarding-title">
                <span>Step 3</span>
                <h1>Add what families need to know</h1>
                <p>This provider-owned information can be changed later by text or in the dashboard.</p>
              </div>
              <div className="onboarding-profile-fields">
                <Field id="onboarding-description" label="Public description">
                  <textarea
                    value={profile.description}
                    onChange={event =>
                      setProfile(current => ({ ...current, description: event.target.value }))}
                    maxLength={320}
                  />
                </Field>
                <div>
                  <Field id="onboarding-hours" label="Regular hours">
                    <Input
                      value={profile.hours}
                      onChange={event =>
                        setProfile(current => ({ ...current, hours: event.target.value }))}
                    />
                  </Field>
                  <Field id="onboarding-languages" label="Languages">
                    <Input
                      value={profile.languages}
                      onChange={event =>
                        setProfile(current => ({ ...current, languages: event.target.value }))}
                    />
                  </Field>
                </div>
              </div>
              <div className="onboarding-openings">
                <span>Openings families should see</span>
                <label>
                  <strong>Infant</strong>
                  <input
                    aria-label="Infant openings"
                    type="number"
                    min="0"
                    value={profile.infantSpots}
                    onChange={event =>
                      setProfile(current => ({
                        ...current,
                        infantSpots: Number(event.target.value),
                      }))}
                  />
                </label>
                <label>
                  <strong>Pre-K</strong>
                  <input
                    aria-label="Pre-K openings"
                    type="number"
                    min="0"
                    value={profile.preKSpots}
                    onChange={event =>
                      setProfile(current => ({
                        ...current,
                        preKSpots: Number(event.target.value),
                      }))}
                  />
                </label>
              </div>
            </>
          )}

          {step === 3 && selected && (
            <>
              <div className="onboarding-title">
                <span>Step 4</span>
                <h1>Review your provider plan</h1>
                <p>This is a prototype. No payment will be collected.</p>
              </div>
              <div className="onboarding-plan">
                <div>
                  <Badge tone="brand">Standard plan</Badge>
                  <strong>$65 <small>/ month</small></strong>
                  <p>Cancel anytime. No setup fee.</p>
                </div>
                <ul>
                  <li><Icon name="check" size={15}/>Live openings and freshness reminders</li>
                  <li><Icon name="check" size={15}/>Tours and waitlist management</li>
                  <li><Icon name="check" size={15}/>Provider Assistant by text</li>
                  <li><Icon name="check" size={15}/>Enhanced profile and posts</li>
                </ul>
              </div>
              <div className="onboarding-summary">
                <span>Center</span><strong>{selected.name}</strong>
                <span>Openings</span>
                <strong>{profile.infantSpots} infant · {profile.preKSpots} Pre-K</strong>
              </div>
              <label className="onboarding-confirm">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={event => setAccepted(event.target.checked)}
                />
                <span>I understand this is a demonstration and no payment will be collected.</span>
              </label>
              {finishAction.error && (
                <div className="onboarding-error" role="alert">
                  We couldn’t finish setup: {finishAction.error.message}
                </div>
              )}
            </>
          )}

          <footer className="onboarding-actions">
            {step > 0 ? (
              <Button variant="ghost" icon="chev-left" onClick={() => setStep(value => value - 1)}>
                Back
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => go('home')}>Cancel</Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button
                variant="primary"
                iconRight="arrow-right"
                disabled={!canContinue}
                onClick={() => setStep(value => value + 1)}
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="primary"
                icon="check"
                disabled={!canContinue || finishAction.loading}
                onClick={finish}
              >
                {finishAction.loading ? 'Starting…' : 'Start provider demo'}
              </Button>
            )}
          </footer>
        </section>
      </main>
    </div>
  );
};
